import type { BrandNoteDTO } from "@nlc/shared-types";

import { BrandNoteTypeEnum, isCustomerRole } from "@nlc/shared-types";
import { useMemo, useState } from "react";

import { useUser } from "@/hooks/useUser";

interface UseKnowledgeFiltersParams {
  readonly notes: BrandNoteDTO[] | undefined;
}

interface TabItem {
  count: number;
  id: string;
  label: string;
}

interface UseKnowledgeFiltersResult {
  filteredNotes: BrandNoteDTO[];
  lastUpdatedDate: Date | null;
  searchQuery: string;
  selectedTab: string;
  setSearchQuery: (value: string) => void;
  setSelectedTab: (value: string) => void;
  tabItems: TabItem[];
}

export function useKnowledgeFilters({
  notes,
}: UseKnowledgeFiltersParams): UseKnowledgeFiltersResult {
  const { data: userData } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("all");

  const isCurrentUserCustomer =
    userData !== undefined && isCustomerRole(userData.user.roleId);

  const visibleNotes = useMemo(() => {
    if (!notes) return [];
    if (isCurrentUserCustomer) {
      return notes.filter((note) => note.isCustomerNote);
    }

    return notes;
  }, [notes, isCurrentUserCustomer]);

  const filteredNotes = useMemo(
    () =>
      visibleNotes.filter((note) => {
        const matchesSearch =
          note.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          note.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTab =
          selectedTab === "all" || note.noteType === selectedTab;

        return matchesSearch && matchesTab;
      }),
    [visibleNotes, searchQuery, selectedTab],
  );

  const tabItems = useMemo(() => {
    const allCount = visibleNotes.length;
    const requirementsCount = visibleNotes.filter(
      (n) => n.noteType === BrandNoteTypeEnum.REQUIREMENTS,
    ).length;
    const preferencesCount = visibleNotes.filter(
      (n) => n.noteType === BrandNoteTypeEnum.PREFERENCES,
    ).length;
    const contextCount = visibleNotes.filter(
      (n) => n.noteType === BrandNoteTypeEnum.CONTEXT_REFERENCES,
    ).length;

    return [
      { count: allCount, id: "all", label: "All" },
      {
        count: requirementsCount,
        id: BrandNoteTypeEnum.REQUIREMENTS,
        label: "Requirements",
      },
      {
        count: preferencesCount,
        id: BrandNoteTypeEnum.PREFERENCES,
        label: "Preferences",
      },
      {
        count: contextCount,
        id: BrandNoteTypeEnum.CONTEXT_REFERENCES,
        label: "Context",
      },
    ];
  }, [visibleNotes]);

  const lastUpdatedDate = useMemo(() => {
    if (visibleNotes.length === 0) return null;
    const dates = visibleNotes.map((n) => new Date(n.updatedAt ?? n.createdAt));

    return new Date(Math.max(...dates.map((d) => d.getTime())));
  }, [visibleNotes]);

  return {
    filteredNotes,
    lastUpdatedDate,
    searchQuery,
    selectedTab,
    setSearchQuery,
    setSelectedTab,
    tabItems,
  };
}
